import React, { useState } from 'react';
import { FaHighlighter, FaCheck, FaPen, FaEraser } from 'react-icons/fa';

const Highlighter = ({ 
  onColorSelect, 
  selectedColor, 
  onHighlightModeToggle, 
  isHighlightMode, 
  selectedShapeId, 
  updateShape,
  onDrawingModeToggle,
  isDrawingMode 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState('highlight'); // 'highlight' or 'draw'
  const [selectedLineWidth, setSelectedLineWidth] = useState(8);

  const highlightColors = [
    { id: 'yellow', name: 'Yellow', color: '#FFEB3B', hex: 0xFFEB3B },
    { id: 'green', name: 'Green', color: '#4CAF50', hex: 0x4CAF50 },
    { id: 'blue', name: 'Blue', color: '#2196F3', hex: 0x2196F3 },
    { id: 'red', name: 'Red', color: '#F44336', hex: 0xF44336 },
    { id: 'orange', name: 'Orange', color: '#FF9800', hex: 0xFF9800 },
    { id: 'purple', name: 'Purple', color: '#9C27B0', hex: 0x9C27B0 },
    { id: 'pink', name: 'Pink', color: '#E91E63', hex: 0xE91E63 },
    { id: 'cyan', name: 'Cyan', color: '#00BCD4', hex: 0x00BCD4 },
    { id: 'lime', name: 'Lime', color: '#CDDC39', hex: 0xCDDC39 },
    { id: 'amber', name: 'Amber', color: '#FFC107', hex: 0xFFC107 },
    { id: 'indigo', name: 'Indigo', color: '#3F51B5', hex: 0x3F51B5 },
    { id: 'teal', name: 'Teal', color: '#009688', hex: 0x009688 },
    { id: 'black', name: 'Black', color: '#000000', hex: 0x000000 },
    { id: 'gray', name: 'Gray', color: '#666666', hex: 0x666666 },
  ];

  const lineWidths = [
    { value: 2, name: 'Thin' },
    { value: 4, name: 'Medium' },
    { value: 8, name: 'Thick' },
    { value: 12, name: 'Extra Thick' },
  ];

  const handleColorSelect = (color) => {
    onColorSelect(color);
    
    if (selectedTool === 'highlight') {
      // Apply highlight to selected shape if available
      if (selectedShapeId && updateShape) {
        updateShape(selectedShapeId, { highlightColor: color });
        console.log('✅ Applied highlight color:', color.name, 'to shape:', selectedShapeId);
      }
    } else {
      // For drawing mode, just set the color
      console.log('✅ Selected drawing color:', color.name);
    }
    
    setIsOpen(false);
  };

  const handleToggleHighlightMode = () => {
    setSelectedTool('highlight');
    onHighlightModeToggle(!isHighlightMode);
    if (isDrawingMode) {
      onDrawingModeToggle(false);
    }
  };

  const handleToggleDrawingMode = () => {
    setSelectedTool('draw');
    onDrawingModeToggle(!isDrawingMode);
    if (isHighlightMode) {
      onHighlightModeToggle(false);
    }
  };

  const handleLineWidthSelect = (width) => {
    setSelectedLineWidth(width);
    console.log('✅ Selected line width:', width);
  };

  const activeMode = isHighlightMode ? 'highlight' : isDrawingMode ? 'draw' : null;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '8px 12px',
          border: 'none',
          borderRadius: '6px',
          background: activeMode
            ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)' 
            : 'linear-gradient(135deg, #1a1a1a 0%, #404040 100%)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: '600',
          width: '100%',
          justifyContent: 'flex-start'
        }}
      >
        {isDrawingMode ? <FaPen /> : <FaHighlighter />}
        {isDrawingMode ? 'Drawing Pen' : 'Highlighter'}
        {selectedColor && (
          <div
            style={{
              width: '16px',
              height: '16px',
              backgroundColor: selectedColor.color,
              borderRadius: '2px',
              border: '1px solid white'
            }}
          />
        )}
      </button>

      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: '200px', // Fixed position from top of viewport
            right: '420px', // Fixed position from right of viewport (outside toolbar)
            background: 'linear-gradient(135deg, #1a1a1a 0%, #404040 100%)',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '16px',
            zIndex: 2500,
            minWidth: '320px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}
        >
          <div style={{ marginBottom: '16px', fontWeight: 'bold', color: 'white' }}>
            Drawing Tools
          </div>
          
          {/* Tool Selection */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ color: 'white', fontSize: '12px', marginBottom: '8px' }}>
              Select Tool:
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleToggleHighlightMode}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: '6px',
                  background: isHighlightMode 
                    ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)' 
                    : 'linear-gradient(135deg, #333 0%, #555 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  justifyContent: 'center'
                }}
              >
                <FaHighlighter />
                Shape Highlight
              </button>
              <button
                onClick={handleToggleDrawingMode}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: '6px',
                  background: isDrawingMode 
                    ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)' 
                    : 'linear-gradient(135deg, #333 0%, #555 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  justifyContent: 'center'
                }}
              >
                <FaPen />
                Draw/Mark
              </button>
            </div>
          </div>

          {/* Line Width Selection (for drawing mode only) */}
          {isDrawingMode && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: 'white', fontSize: '12px', marginBottom: '8px' }}>
                Line Width:
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {lineWidths.map((width) => (
                  <button
                    key={width.value}
                    onClick={() => handleLineWidthSelect(width.value)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: 'none',
                      borderRadius: '4px',
                      background: selectedLineWidth === width.value
                        ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'
                        : 'linear-gradient(135deg, #333 0%, #555 100%)',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '10px',
                      fontWeight: '600',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '2px'
                    }}
                  >
                    <div 
                      style={{
                        width: '20px',
                        height: `${width.value}px`,
                        backgroundColor: 'white',
                        borderRadius: '2px'
                      }}
                    />
                    {width.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Colors */}
          <div style={{ marginBottom: '12px', color: 'white', fontSize: '12px' }}>
            Colors:
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px' }}>
            {highlightColors.map((colorOption) => (
              <div
                key={colorOption.id}
                onClick={() => handleColorSelect(colorOption)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  background: selectedColor?.id === colorOption.id ? 'rgba(255,255,255,0.2)' : 'transparent',
                  transition: 'background 0.2s ease'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: colorOption.color,
                    border: '2px solid #333',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}
                >
                  {selectedColor?.id === colorOption.id && (
                    <FaCheck style={{ color: 'white', fontSize: '14px', textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }} />
                  )}
                </div>
                <div style={{ fontSize: '9px', color: 'white', textAlign: 'center' }}>
                  {colorOption.name}
                </div>
              </div>
            ))}
          </div>

          {/* Status */}
          <div style={{ 
            padding: '8px 12px',
            borderRadius: '6px',
            background: 'rgba(0,0,0,0.3)',
            color: 'white',
            fontSize: '12px',
            textAlign: 'center'
          }}>
            {isDrawingMode ? (
              <>
                <strong>Drawing Mode Active</strong>
                <br />
                <span style={{ opacity: 0.7 }}>Click and drag to draw markings</span>
              </>
            ) : isHighlightMode ? (
              <>
                <strong>Highlight Mode Active</strong>
                <br />
                <span style={{ opacity: 0.7 }}>Click shapes to highlight them</span>
              </>
            ) : (
              <span style={{ opacity: 0.7 }}>Select a tool to start</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Highlighter; 